/**
 * Wizard Navigation Component
 *
 * Back/Next buttons with validation gates.
 *
 * Session 4.1: Wizard State Machine + Steps 1-2
 */

import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import { useWizard } from '../context/WizardContext';

interface WizardNavigationProps {
  /** Custom label for next button */
  nextLabel?: string;
  /** Hide back button */
  showBack?: boolean;
  /** Custom next handler (called before navigation) */
  onNext?: () => void | Promise<void>;
  /** Whether next action is loading */
  isNextLoading?: boolean;
  /** Disable next button */
  isNextDisabled?: boolean;
}

/**
 * Navigation buttons for wizard steps.
 */
export function WizardNavigation({
  nextLabel,
  showBack = true,
  onNext,
  isNextLoading = false,
  isNextDisabled = false,
}: WizardNavigationProps) {
  const { isFirstStep, isLastStep, canGoNext, canGoPrev, nextStep, prevStep } =
    useWizard();

  const handleNext = async () => {
    if (onNext) {
      await onNext();
    } else {
      nextStep();
    }
  };

  // Determine next button label
  const getNextLabel = () => {
    if (nextLabel) return nextLabel;
    if (isLastStep) return 'Finish';
    return 'Continue';
  };

  return (
    <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
      {/* Back button */}
      <div>
        {showBack && !isFirstStep && (
          <button
            type="button"
            onClick={prevStep}
            disabled={!canGoPrev}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>
        )}
      </div>

      {/* Next button */}
      <div>
        {!isLastStep && (
          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoNext || isNextLoading || isNextDisabled}
            className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isNextLoading ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </>
            ) : (
              <>
                {getNextLabel()}
                <ArrowRightIcon className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
